/**
 * Timezone Utility Module
 *
 * Provides centralized timezone handling for the VastiPost application.
 * Uses Luxon for robust timezone conversions.
 *
 * Priority order for timezone:
 * 1. Brand timezone (if set)
 * 2. User timezone (if set)
 * 3. UTC (fallback)
 *
 * @example
 * ```typescript
 * import { getEffectiveTimezone, formatTimeInTimezone } from "@/lib/timezone";
 *
 * const tz = getEffectiveTimezone(userTimezone, brandTimezone);
 * const timeDisplay = formatTimeInTimezone(scheduledAt, tz, { use24Hour: false });
 * // Returns "3:00 PM" or "15:00" based on format
 * ```
 */

import { DateTime } from "luxon";

/**
 * Default timezone when no user or brand timezone is set
 */
export const DEFAULT_TIMEZONE = "UTC";

/**
 * Common timezone options for UI selection
 * Organized by region for better UX
 */
export const TIMEZONE_OPTIONS = [
  // Americas
  { value: "America/New_York", label: "Eastern Time (ET)", region: "Americas" },
  { value: "America/Chicago", label: "Central Time (CT)", region: "Americas" },
  { value: "America/Denver", label: "Mountain Time (MT)", region: "Americas" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)", region: "Americas" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)", region: "Americas" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)", region: "Americas" },
  { value: "America/Toronto", label: "Toronto (ET)", region: "Americas" },
  { value: "America/Vancouver", label: "Vancouver (PT)", region: "Americas" },
  { value: "America/Mexico_City", label: "Mexico City (CST)", region: "Americas" },
  { value: "America/Sao_Paulo", label: "São Paulo (BRT)", region: "Americas" },
  { value: "America/Buenos_Aires", label: "Buenos Aires (ART)", region: "Americas" },

  // Europe
  { value: "Europe/London", label: "London (GMT/BST)", region: "Europe" },
  { value: "Europe/Paris", label: "Paris (CET/CEST)", region: "Europe" },
  { value: "Europe/Berlin", label: "Berlin (CET/CEST)", region: "Europe" },
  { value: "Europe/Amsterdam", label: "Amsterdam (CET/CEST)", region: "Europe" },
  { value: "Europe/Madrid", label: "Madrid (CET/CEST)", region: "Europe" },
  { value: "Europe/Rome", label: "Rome (CET/CEST)", region: "Europe" },
  { value: "Europe/Stockholm", label: "Stockholm (CET/CEST)", region: "Europe" },
  { value: "Europe/Moscow", label: "Moscow (MSK)", region: "Europe" },

  // Asia & Pacific
  { value: "Asia/Dubai", label: "Dubai (GST)", region: "Asia & Pacific" },
  { value: "Asia/Kolkata", label: "India (IST)", region: "Asia & Pacific" },
  { value: "Asia/Singapore", label: "Singapore (SGT)", region: "Asia & Pacific" },
  { value: "Asia/Hong_Kong", label: "Hong Kong (HKT)", region: "Asia & Pacific" },
  { value: "Asia/Shanghai", label: "Shanghai (CST)", region: "Asia & Pacific" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)", region: "Asia & Pacific" },
  { value: "Asia/Seoul", label: "Seoul (KST)", region: "Asia & Pacific" },
  { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)", region: "Asia & Pacific" },
  { value: "Australia/Melbourne", label: "Melbourne (AEST/AEDT)", region: "Asia & Pacific" },
  { value: "Pacific/Auckland", label: "Auckland (NZST/NZDT)", region: "Asia & Pacific" },

  // Africa & Middle East
  { value: "Africa/Cairo", label: "Cairo (EET)", region: "Africa & Middle East" },
  { value: "Africa/Johannesburg", label: "Johannesburg (SAST)", region: "Africa & Middle East" },
  { value: "Africa/Lagos", label: "Lagos (WAT)", region: "Africa & Middle East" },
  { value: "Asia/Jerusalem", label: "Jerusalem (IST)", region: "Africa & Middle East" },

  // Universal
  { value: "UTC", label: "UTC (Coordinated Universal Time)", region: "Universal" },
] as const;

/**
 * Get timezone regions for grouped dropdown display
 */
export function getTimezonesByRegion(): Record<string, typeof TIMEZONE_OPTIONS[number][]> {
  const grouped: Record<string, typeof TIMEZONE_OPTIONS[number][]> = {};

  for (const tz of TIMEZONE_OPTIONS) {
    if (!grouped[tz.region]) {
      grouped[tz.region] = [];
    }
    grouped[tz.region].push(tz);
  }

  return grouped;
}

/**
 * Determine the effective timezone to use for display
 *
 * Priority:
 * 1. Brand timezone (if set) - overrides user preference for brand consistency
 * 2. User timezone (if set) - user's personal preference
 * 3. UTC - fallback when nothing is configured
 *
 * @param userTimezone - User's configured timezone
 * @param brandTimezone - Brand's configured timezone
 * @returns The timezone to use for display (IANA timezone string)
 */
export function getEffectiveTimezone(
  userTimezone?: string | null,
  brandTimezone?: string | null
): string {
  // Brand timezone takes priority (brand consistency across team members)
  if (brandTimezone && isValidTimezone(brandTimezone)) {
    return brandTimezone;
  }

  // User timezone as fallback
  if (userTimezone && isValidTimezone(userTimezone)) {
    return userTimezone;
  }

  // Default to UTC
  return DEFAULT_TIMEZONE;
}

/**
 * Check if a timezone string is valid
 *
 * @param timezone - IANA timezone string to validate
 * @returns true if valid
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    const dt = DateTime.now().setZone(timezone);
    return dt.isValid && dt.zone.isValid;
  } catch {
    return false;
  }
}

/**
 * Format options for time display
 */
export interface TimeFormatOptions {
  /** Use 24-hour format (e.g., "15:00") vs 12-hour (e.g., "3:00 PM") */
  use24Hour?: boolean;
  /** Include seconds in the output */
  includeSeconds?: boolean;
}

/**
 * Format a UTC date/time to a time string in the specified timezone
 *
 * @param date - Date object (assumed UTC or with timezone info)
 * @param timezone - Target timezone (IANA string)
 * @param options - Formatting options
 * @returns Formatted time string (e.g., "3:00 PM" or "15:00")
 *
 * @example
 * ```typescript
 * formatTimeInTimezone(new Date("2026-01-17T18:00:00Z"), "America/New_York", { use24Hour: false })
 * // Returns "1:00 PM" (EST is UTC-5)
 * ```
 */
export function formatTimeInTimezone(
  date: Date | string,
  timezone: string,
  options: TimeFormatOptions = {}
): string {
  const { use24Hour = false, includeSeconds = false } = options;

  const dt = DateTime.fromJSDate(date instanceof Date ? date : new Date(date), { zone: "utc" })
    .setZone(timezone);

  if (!dt.isValid) {
    console.warn(`[timezone] Invalid date or timezone: ${date}, ${timezone}`);
    return use24Hour ? "00:00" : "12:00 AM";
  }

  if (use24Hour) {
    return includeSeconds
      ? dt.toFormat("HH:mm:ss")
      : dt.toFormat("HH:mm");
  } else {
    return includeSeconds
      ? dt.toFormat("h:mm:ss a")
      : dt.toFormat("h:mm a");
  }
}

/**
 * Get just the time portion for schedule display (e.g., "3:00")
 *
 * @param date - Date object
 * @param timezone - Target timezone
 * @param use24Hour - Whether to use 24-hour format
 * @returns Time string without AM/PM (e.g., "3:00" or "15:00")
 */
export function formatTimeText(
  date: Date | string,
  timezone: string,
  use24Hour = false
): string {
  const dt = DateTime.fromJSDate(date instanceof Date ? date : new Date(date), { zone: "utc" })
    .setZone(timezone);

  if (!dt.isValid) {
    return use24Hour ? "00:00" : "12:00";
  }

  return use24Hour ? dt.toFormat("HH:mm") : dt.toFormat("h:mm");
}

/**
 * Get the meridiem (AM/PM) for a given date in a timezone
 *
 * @param date - Date object
 * @param timezone - Target timezone
 * @returns "AM" or "PM"
 */
export function getTimeMeridiem(
  date: Date | string,
  timezone: string
): "AM" | "PM" {
  const dt = DateTime.fromJSDate(date instanceof Date ? date : new Date(date), { zone: "utc" })
    .setZone(timezone);

  if (!dt.isValid) {
    return "AM";
  }

  return dt.hour < 12 ? "AM" : "PM";
}

/**
 * Format options for date display
 */
export interface DateFormatOptions {
  /** Include year in the output */
  includeYear?: boolean;
  /** Use short month name (e.g., "Jan" vs "January") */
  shortMonth?: boolean;
  /** Include day of week */
  includeDayOfWeek?: boolean;
}

/**
 * Format a UTC date to a date string in the specified timezone
 *
 * @param date - Date object (assumed UTC)
 * @param timezone - Target timezone
 * @param options - Formatting options
 * @returns Formatted date string (e.g., "Wednesday, January 15")
 *
 * @example
 * ```typescript
 * formatDateInTimezone(new Date("2026-01-17T02:00:00Z"), "America/New_York")
 * // Returns "Thursday, January 16" (because 2 AM UTC is 9 PM Jan 16 in NYC)
 * ```
 */
export function formatDateInTimezone(
  date: Date | string,
  timezone: string,
  options: DateFormatOptions = {}
): string {
  const { includeYear = false, shortMonth = false, includeDayOfWeek = true } = options;

  const dt = DateTime.fromJSDate(date instanceof Date ? date : new Date(date), { zone: "utc" })
    .setZone(timezone);

  if (!dt.isValid) {
    console.warn(`[timezone] Invalid date or timezone: ${date}, ${timezone}`);
    return "Invalid Date";
  }

  // Build format string
  let format = "";

  if (includeDayOfWeek) {
    format += "EEEE, "; // Full weekday name
  }

  format += shortMonth ? "MMM d" : "MMMM d";

  if (includeYear) {
    format += ", yyyy";
  }

  return dt.toFormat(format);
}

/**
 * Get hour and minute values in the specified timezone
 * Useful for time picker initialization
 *
 * @param date - Date object (assumed UTC)
 * @param timezone - Target timezone
 * @returns Object with hours (0-23) and minutes (0-59)
 */
export function getTimeComponents(
  date: Date | string,
  timezone: string
): { hours: number; minutes: number } {
  const dt = DateTime.fromJSDate(date instanceof Date ? date : new Date(date), { zone: "utc" })
    .setZone(timezone);

  if (!dt.isValid) {
    return { hours: 0, minutes: 0 };
  }

  return { hours: dt.hour, minutes: dt.minute };
}

/**
 * Get the calendar date components for an instant in the specified timezone.
 * This avoids browser-local date shifts when a brand operates elsewhere.
 */
export function getDateComponents(
  date: Date | string,
  timezone: string
): { year: number; month: number; day: number } {
  const dt = DateTime.fromJSDate(date instanceof Date ? date : new Date(date), { zone: "utc" })
    .setZone(timezone);

  if (!dt.isValid) {
    const fallback = date instanceof Date ? date : new Date(date);
    return {
      year: fallback.getUTCFullYear(),
      month: fallback.getUTCMonth() + 1,
      day: fallback.getUTCDate(),
    };
  }

  return { year: dt.year, month: dt.month, day: dt.day };
}

/**
 * Convert a local time in a timezone to UTC
 * Used when saving times from the time picker
 *
 * @param date - Date representing the base date
 * @param hour - Hour in local timezone (0-23)
 * @param minute - Minute (0-59)
 * @param timezone - The timezone the hour/minute are in
 * @returns UTC Date object
 *
 * @example
 * ```typescript
 * // User in NYC sets time to 3:00 PM on Jan 17
 * const utc = localTimeToUTC(baseDate, 15, 0, "America/New_York");
 * // Returns Date representing 8:00 PM UTC (EST is UTC-5)
 * ```
 */
export function localTimeToUTC(
  date: Date | string,
  hour: number,
  minute: number,
  timezone: string
): Date {
  const baseDate = date instanceof Date ? date : new Date(date);

  // Create a DateTime in the specified timezone with the given hour/minute
  const dt = DateTime.fromObject(
    {
      year: baseDate.getUTCFullYear(),
      month: baseDate.getUTCMonth() + 1, // Luxon uses 1-indexed months
      day: baseDate.getUTCDate(),
      hour,
      minute,
      second: 0,
      millisecond: 0,
    },
    { zone: timezone }
  );

  if (!dt.isValid) {
    console.warn(`[timezone] Failed to create DateTime: ${hour}:${minute} in ${timezone}`);
    return baseDate;
  }

  // Convert to UTC and return as JS Date
  return dt.toUTC().toJSDate();
}

/**
 * Get a human-readable label for a timezone
 *
 * @param timezone - IANA timezone string
 * @returns Human-readable label or the original string if not found
 */
export function getTimezoneLabel(timezone: string): string {
  const found = TIMEZONE_OPTIONS.find(opt => opt.value === timezone);
  if (found) {
    return found.label;
  }

  // Try to format a nice label from the IANA string
  // e.g., "America/New_York" -> "America/New York"
  return timezone.replace(/_/g, " ");
}

/**
 * Get the current offset for a timezone in hours
 *
 * @param timezone - IANA timezone string
 * @returns Offset in hours (e.g., -5 for EST, +1 for CET)
 */
export function getTimezoneOffset(timezone: string): number {
  const dt = DateTime.now().setZone(timezone);
  if (!dt.isValid) {
    return 0;
  }
  return dt.offset / 60; // Luxon offset is in minutes
}

/**
 * Format timezone offset for display (e.g., "UTC-5" or "UTC+1")
 *
 * @param timezone - IANA timezone string
 * @returns Formatted offset string
 */
export function formatTimezoneOffset(timezone: string): string {
  const offset = getTimezoneOffset(timezone);
  if (offset === 0) {
    return "UTC";
  }
  const sign = offset > 0 ? "+" : "";
  return `UTC${sign}${offset}`;
}
